import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  tPromotion,
  targetTypeForScope,
  type MenuCategoryOption,
  type MenuItemOption,
} from "@/features/staff/promotions/promotions"

type Props = {
  scope: string
  items: MenuItemOption[]
  categories: MenuCategoryOption[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

type Choice = {
  id: number
  name: string
  hint: string | null
}

/**
 * Which dishes or which sections of the menu a scoped rule reaches.
 *
 * A checkbox list rather than a multi-select, because the question a manager is
 * answering is "which of my dishes", and a menu is browsed rather than searched
 * from memory. The list is bounded by the restaurant's own menu, which is
 * already the size a person scrolls on the menu screen.
 *
 * **A bill-scoped rule renders nothing at all.** It reaches everything by
 * definition and holds no targets — showing a disabled picker would suggest
 * there is a narrowing available that the server would then ignore.
 *
 * The empty case is a restaurant with no menu yet, and it says so rather than
 * rendering an empty box: a manager staring at a blank picker has no way to tell
 * "nothing matched" from "this is broken".
 */
export function PromotionTargetPicker({ scope, items, categories, selectedIds, onChange }: Props) {
  const type = targetTypeForScope(scope)
  if (type === null) return null

  const choices: Choice[] =
    type === "items"
      ? items.map((item) => ({ id: item.id, name: item.name, hint: item.categoryName }))
      : categories.map((category) => ({ id: category.id, name: category.name, hint: null }))

  const toggle = (id: number, checked: boolean) => {
    onChange(checked ? [...selectedIds, id] : selectedIds.filter((selected) => selected !== id))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label>{tPromotion(type === "items" ? "targets_items_label" : "targets_categories_label")}</Label>
        <span className="text-muted-foreground text-xs tabular-nums" data-testid="promotion-targets-count">
          {tPromotion("targets_selected", { count: selectedIds.length })}
        </span>
      </div>

      {choices.length === 0 ? (
        <p
          className="text-muted-foreground rounded-lg border border-dashed px-4 py-3 text-sm"
          data-testid="promotion-targets-empty"
        >
          {tPromotion(type === "items" ? "targets_items_empty" : "targets_categories_empty")}
        </p>
      ) : (
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border px-4 py-2">
          {choices.map((choice) => {
            const inputId = `promotion-target-${type}-${choice.id}`

            return (
              <div key={choice.id} className="flex min-h-11 items-center gap-2 md:min-h-9">
                <Checkbox
                  id={inputId}
                  checked={selectedIds.includes(choice.id)}
                  onCheckedChange={(checked) => toggle(choice.id, checked === true)}
                  data-testid={`promotion-target-option-${choice.id}`}
                />
                <Label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer font-normal">
                  <span className="truncate">{choice.name}</span>
                  {choice.hint && (
                    <span className="text-muted-foreground text-xs">· {choice.hint}</span>
                  )}
                </Label>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
